// src/pages/university-admin/TopicOversight.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    Box,
    Grid,
    Card,
    CardContent,
    Avatar,
    Chip,
    LinearProgress,
    useTheme,
    Tooltip,
    Alert,
    CircularProgress,
    Fade,
    Zoom,
    Divider,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    InputAdornment,
    Badge,
    Stepper,
    Step,
    StepLabel,
    useMediaQuery
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Search,
    Person,
    School,
    Work,
    CheckCircle,
    Cancel,
    Warning,
    Schedule,
    Visibility,
    Assignment,
    Verified,
    Pending,
    Info,
    RateReview,
    Send,
    Close,
    Article,
    Security,
    TrendingUp,
    People,
    Business,
    HealthAndSafety,
    PriorityHigh
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APITopicOversightList,
    APITopicOversightStats,
    APITopicOversightUpdateOversight,
    APITopicOversightUpdateDepartmental,
    APITopicOversightUpdateEthics,
    APITopicOversightUpdateFinal
} from '../../apiServices/APIs';
import { 
    formatCreatedAtDate, 
    getUserDetails
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
// NOTE: The component file is still named InstitutionAdminBar.js
// but it now renders "Unit Research Lead Portal" as its visible title.
import InstitutionAdminBar from '../../components/InstitutionAdminBar';
import authService from '../../apiServices/authService';
import { FullWidthContainer } from '../../components/FullWidthContainer';

const TopicOversight = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [topics, setTopics] = useState([]);
    const [stats, setStats] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [openOversightDialog, setOpenOversightDialog] = useState(false);
    const [openDepartmentalDialog, setOpenDepartmentalDialog] = useState(false);
    const [openEthicsDialog, setOpenEthicsDialog] = useState(false);
    const [openFinalDialog, setOpenFinalDialog] = useState(false);
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [oversightForm, setOversightForm] = useState({ status: '', priority: '', notes: '', review_status: '' });
    const [departmentalForm, setDepartmentalForm] = useState({ status: '', comments: '' });
    const [ethicsForm, setEthicsForm] = useState({ status: '', comments: '' });
    const [finalForm, setFinalForm] = useState({ status: '', comments: '' });
    const [actionLoading, setActionLoading] = useState(false);
    const showToast = useCustomToast();
    const isMounted = useRef(true);
    const adminId = getUserDetails("Id");

    // Handle logout
    const handleLogout = async () => {
        try {
            await authService.logout();
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchAllData();
        
        return () => {
            isMounted.current = false;
        };
    }, []);

    const fetchAllData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            await Promise.all([
                fetchTopics(),
                fetchStats()
            ]);
        } catch (err) {
            setError("Failed to load topic data");
            console.error("Fetch error:", err);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const fetchTopics = async () => {
        const result = await apiService.list(APITopicOversightList);
        if (result.success && isMounted.current) {
            setTopics(Array.isArray(result.data) ? result.data : []);
        }
    };

    const fetchStats = async () => {
        const result = await apiService.list(APITopicOversightStats);
        if (result.success && result.data && isMounted.current) {
            setStats(result.data);
        }
    };

    const handleUpdateOversight = async () => {
        setActionLoading(true);
        try {
            const formData = new FormData();
            formData.append('admin_id', adminId);
            formData.append('oversight_id', selectedTopic.id);
            formData.append('oversight_status', oversightForm.status);
            if (oversightForm.priority) formData.append('priority_level', oversightForm.priority);
            if (oversightForm.notes) formData.append('oversight_notes', oversightForm.notes);
            if (oversightForm.review_status) formData.append('review_status', oversightForm.review_status);
            
            const result = await apiService.update(APITopicOversightUpdateOversight, formData);
            
            if (result && result.success) {
                showToast("Success", "Oversight status updated", "success");
                fetchAllData();
                handleCloseOversightDialog();
            }
        } catch (error) {
            showToast("Error", "Failed to update oversight", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleUpdateDepartmental = async () => {
        setActionLoading(true);
        try {
            const formData = new FormData();
            formData.append('admin_id', adminId);
            formData.append('oversight_id', selectedTopic.id);
            formData.append('departmental_approval_status', departmentalForm.status);
            if (departmentalForm.comments) formData.append('department_comments', departmentalForm.comments);
            
            const result = await apiService.update(APITopicOversightUpdateDepartmental, formData);
            
            if (result && result.success) {
                showToast("Success", "Departmental approval updated", "success");
                fetchAllData();
                handleCloseDepartmentalDialog();
            }
        } catch (error) {
            showToast("Error", "Failed to update departmental approval", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleUpdateEthics = async () => {
        setActionLoading(true);
        try {
            const formData = new FormData();
            formData.append('admin_id', adminId);
            formData.append('oversight_id', selectedTopic.id);
            formData.append('ethics_committee_status', ethicsForm.status);
            if (ethicsForm.comments) formData.append('ethics_comments', ethicsForm.comments);
            
            const result = await apiService.update(APITopicOversightUpdateEthics, formData);
            
            if (result && result.success) {
                showToast("Success", "Ethics committee status updated", "success");
                fetchAllData();
                handleCloseEthicsDialog();
            }
        } catch (error) {
            showToast("Error", "Failed to update ethics status", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleUpdateFinal = async () => {
        setActionLoading(true);
        try {
            const formData = new FormData();
            formData.append('admin_id', adminId);
            formData.append('oversight_id', selectedTopic.id);
            formData.append('final_approval_status', finalForm.status);
            if (finalForm.comments) formData.append('final_comments', finalForm.comments);
            
            const result = await apiService.update(APITopicOversightUpdateFinal, formData);
            
            if (result && result.success) {
                showToast("Success", "Final approval updated", "success");
                fetchAllData();
                handleCloseFinalDialog();
            }
        } catch (error) {
            showToast("Error", "Failed to update final approval", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleOpenOversightDialog = (topic) => {
        setSelectedTopic(topic);
        setOversightForm({ status: topic.oversight_status, priority: topic.priority_level, notes: topic.oversight_notes || '', review_status: topic.review_status });
        setOpenOversightDialog(true);
    };

    const handleCloseOversightDialog = () => {
        setOpenOversightDialog(false);
        setSelectedTopic(null);
        setOversightForm({ status: '', priority: '', notes: '', review_status: '' });
    };

    const handleOpenDepartmentalDialog = (topic) => {
        setSelectedTopic(topic);
        setDepartmentalForm({ status: topic.departmental_approval_status, comments: topic.department_comments || '' });
        setOpenDepartmentalDialog(true);
    };

    const handleCloseDepartmentalDialog = () => {
        setOpenDepartmentalDialog(false);
        setSelectedTopic(null);
        setDepartmentalForm({ status: '', comments: '' });
    };

    const handleOpenEthicsDialog = (topic) => {
        setSelectedTopic(topic);
        setEthicsForm({ status: topic.ethics_committee_status, comments: topic.ethics_comments || '' });
        setOpenEthicsDialog(true);
    };

    const handleCloseEthicsDialog = () => {
        setOpenEthicsDialog(false);
        setSelectedTopic(null);
        setEthicsForm({ status: '', comments: '' });
    };

    const handleOpenFinalDialog = (topic) => {
        setSelectedTopic(topic);
        setFinalForm({ status: topic.final_approval_status, comments: topic.final_comments || '' });
        setOpenFinalDialog(true);
    };

    const handleCloseFinalDialog = () => {
        setOpenFinalDialog(false);
        setSelectedTopic(null);
        setFinalForm({ status: '', comments: '' });
    };

    const handleOpenViewDialog = (topic) => {
        setSelectedTopic(topic);
        setOpenViewDialog(true);
    };

    const handleCloseViewDialog = () => {
        setOpenViewDialog(false);
        setSelectedTopic(null);
    };

    const getPriorityIcon = (priority) => {
        switch(priority) {
            case 'urgent': return <Warning color="warning" />;
            case 'controversial': return <PriorityHigh color="error" />;
            default: return <Info color="info" />;
        }
    };

    const filteredTopics = topics.filter(topic => 
        topic.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        topic.topic_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        topic.supervisor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        topic.oversight_id?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const pendingCount = topics.filter(t => t.oversight_status === 'pending_review' || t.oversight_status === 'department_review' || t.oversight_status === 'ethics_review').length;

    if (loading) {
        return <LoadingState message="Loading Topic Oversight..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchAllData} title="Unable to Load Topic Oversight" variant="research" fullPage retryText="Retry Loading" />;
    }

    return (
        <>
            <FullWidthContainer >
                <Box sx={{backgroundColor: 'white',borderRadius: 3 }}>
                    <InstitutionAdminBar notificationCount={89} />
                {/* Header Section */}
                <Paper elevation={2} sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    mb: { xs: 2, sm: 3 }, 
                    borderRadius: 3,
                    backgroundColor: 'white',
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        flexWrap: 'wrap', 
                        gap: 2,
                        flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                        <Typography variant="h6" sx={{ 
                            fontWeight: 'bold', 
                            color: theme.palette.primary.main, 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            fontSize: { xs: '1rem', sm: '1.25rem' }
                        }}>
                            <Verified /> Topic Oversight
                            {pendingCount > 0 && (
                                <Chip 
                                    label={`${pendingCount} Pending`} 
                                    color="warning" 
                                    size="small"
                                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                />
                            )}
                        </Typography>
                        <Button 
                            variant="outlined" 
                            color="info" 
                            startIcon={<RefreshIcon />} 
                            onClick={fetchAllData} 
                            sx={{ 
                                textTransform: 'none',
                                width: { xs: '100%', sm: 'auto' }
                            }}
                            size={isMobile ? "small" : "medium"}
                        >
                            Refresh
                        </Button>
                    </Box>
                </Paper>

                {/* Stats Cards */}
                {stats && (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={6} sm={3}>
                            <Card sx={{ 
                                textAlign: 'center', 
                                bgcolor: theme.palette.warning.light + '20',
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'translateY(-4px)' }
                            }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Typography variant="h3" sx={{ 
                                        fontWeight: 'bold',
                                        fontSize: { xs: '1.5rem', sm: '2rem' }
                                    }}>
                                        {stats.pending}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                        Pending Review
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Card sx={{ 
                                textAlign: 'center', 
                                bgcolor: theme.palette.success.light + '20',
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'translateY(-4px)' }
                            }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Typography variant="h3" sx={{ 
                                        fontWeight: 'bold',
                                        fontSize: { xs: '1.5rem', sm: '2rem' }
                                    }}>
                                        {stats.approved}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                        Approved
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Card sx={{ 
                                textAlign: 'center', 
                                bgcolor: theme.palette.error.light + '20',
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'translateY(-4px)' }
                            }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Typography variant="h3" sx={{ 
                                        fontWeight: 'bold',
                                        fontSize: { xs: '1.5rem', sm: '2rem' }
                                    }}>
                                        {stats.high_similarity}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                        High Similarity
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Card sx={{ 
                                textAlign: 'center', 
                                bgcolor: theme.palette.info.light + '20',
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'translateY(-4px)' }
                            }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Typography variant="h3" sx={{ 
                                        fontWeight: 'bold',
                                        fontSize: { xs: '1.5rem', sm: '2rem' }
                                    }}>
                                        {stats.average_review_time_days}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                        Avg Review (Days)
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* Search Bar */}
                <Paper sx={{ 
                    mb: 3, 
                    p: { xs: 1.5, sm: 2 }, 
                    display: 'flex', 
                    alignItems: 'center',
                    backgroundColor: 'white',
                    borderRadius: 2
                }}>
                    <Search sx={{ mx: 1, color: 'text.secondary' }} />
                    <TextField 
                        fullWidth 
                        placeholder="Search by student, topic, supervisor..." 
                        variant="standard" 
                        value={searchTerm} 
                        onChange={(e) => setSearchTerm(e.target.value)} 
                        InputProps={{ disableUnderline: true }}
                        size={isMobile ? "small" : "medium"}
                    />
                </Paper>
                </Box>
                {/* Topics Table */}
                <Paper elevation={2} sx={{ 
                    borderRadius: 3, 
                    overflow: 'auto',
                    backgroundColor: 'white',
                }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table sx={{ minWidth: { xs: 800, sm: 900, md: 1100 } }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: theme.palette.grey[100] }}>
                                    <TableCell><strong>Student / Topic</strong></TableCell>
                                    <TableCell><strong>Supervisor</strong></TableCell>
                                    <TableCell><strong>Submitted</strong></TableCell>
                                    <TableCell><strong>Similarity</strong></TableCell>
                                    <TableCell><strong>Priority</strong></TableCell>
                                    <TableCell><strong>Status</strong></TableCell>
                                    <TableCell align="center"><strong>Actions</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredTopics.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((topic) => (
                                    <TableRow key={topic.id} hover>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2" sx={{ 
                                                    fontWeight: 500,
                                                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                                }}>
                                                    {topic.student_name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                    {topic.topic_title?.substring(0, isMobile ? 40 : 50)}...
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                            {topic.supervisor_name}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                                                {formatCreatedAtDate(topic.submission_date)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                                                {topic.days_since_submission} days ago
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={`${topic.similarity_score || 0}%`} 
                                                size="small" 
                                                color={topic.similarity_color} 
                                                variant="outlined"
                                                sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                icon={getPriorityIcon(topic.priority_level)} 
                                                label={topic.formatted_priority_level} 
                                                size="small" 
                                                color={topic.priority_level_color}
                                                sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                icon={topic.oversight_status_icon} 
                                                label={topic.formatted_oversight_status} 
                                                size="small" 
                                                color={topic.oversight_status_color}
                                                sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                                                <Tooltip title="View Details">
                                                    <IconButton size="small" onClick={() => handleOpenViewDialog(topic)}>
                                                        <Visibility fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Update Oversight">
                                                    <IconButton size="small" color="primary" onClick={() => handleOpenOversightDialog(topic)}>
                                                        <Assignment fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Departmental Approval">
                                                    <IconButton size="small" onClick={() => handleOpenDepartmentalDialog(topic)}>
                                                        <Business fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Ethics Review">
                                                    <IconButton size="small" onClick={() => handleOpenEthicsDialog(topic)}>
                                                        <HealthAndSafety fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Final Approval">
                                                    <IconButton size="small" color="success" onClick={() => handleOpenFinalDialog(topic)}>
                                                        <Verified fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={filteredTopics.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, p) => setPage(p)}
                        onRowsPerPageChange={(e) => { 
                            setRowsPerPage(parseInt(e.target.value, 10)); 
                            setPage(0); 
                        }}
                        sx={{
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                fontSize: { xs: '0.7rem', sm: '0.875rem' }
                            }
                        }}
                    />
                </Paper>

                {/* Update Oversight Dialog */}
                <Dialog 
                    open={openOversightDialog} 
                    onClose={handleCloseOversightDialog} 
                    maxWidth="sm" 
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Update Oversight Status
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <FormControl fullWidth sx={{ mb: 2 }} size={isMobile ? "small" : "medium"}>
                                <InputLabel>Oversight Status</InputLabel>
                                <Select 
                                    value={oversightForm.status} 
                                    onChange={(e) => setOversightForm({ ...oversightForm, status: e.target.value })} 
                                    label="Oversight Status"
                                >
                                    <MenuItem value="pending_review">Pending Review</MenuItem>
                                    <MenuItem value="department_review">Department Review</MenuItem>
                                    <MenuItem value="ethics_review">Ethics Review</MenuItem>
                                    <MenuItem value="approved">Approved</MenuItem>
                                    <MenuItem value="rejected">Rejected</MenuItem>
                                    <MenuItem value="appealed">Appealed</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl fullWidth sx={{ mb: 2 }} size={isMobile ? "small" : "medium"}>
                                <InputLabel>Priority Level</InputLabel>
                                <Select 
                                    value={oversightForm.priority} 
                                    onChange={(e) => setOversightForm({ ...oversightForm, priority: e.target.value })} 
                                    label="Priority Level"
                                >
                                    <MenuItem value="normal">Normal</MenuItem>
                                    <MenuItem value="urgent">Urgent</MenuItem>
                                    <MenuItem value="controversial">Controversial</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl fullWidth sx={{ mb: 2 }} size={isMobile ? "small" : "medium"}>
                                <InputLabel>Review Status</InputLabel>
                                <Select 
                                    value={oversightForm.review_status} 
                                    onChange={(e) => setOversightForm({ ...oversightForm, review_status: e.target.value })} 
                                    label="Review Status"
                                >
                                    <MenuItem value="pending_review">Pending Review</MenuItem>
                                    <MenuItem value="under_review">Under Review</MenuItem>
                                    <MenuItem value="revision_requested">Revision Requested</MenuItem>
                                    <MenuItem value="approved">Approved</MenuItem>
                                    <MenuItem value="rejected">Rejected</MenuItem>
                                    <MenuItem value="escalated">Escalated</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField 
                                fullWidth 
                                label="Oversight Notes" 
                                multiline 
                                rows={3} 
                                value={oversightForm.notes} 
                                onChange={(e) => setOversightForm({ ...oversightForm, notes: e.target.value })}
                                size={isMobile ? "small" : "medium"}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button onClick={handleCloseOversightDialog} size={isMobile ? "small" : "medium"}>
                            Cancel
                        </Button>
                        <Button onClick={handleUpdateOversight} variant="contained" disabled={actionLoading} size={isMobile ? "small" : "medium"}>
                            {actionLoading ? <CircularProgress size={20} /> : 'Update'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Departmental Approval Dialog */}
                <Dialog 
                    open={openDepartmentalDialog} 
                    onClose={handleCloseDepartmentalDialog} 
                    maxWidth="sm" 
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Departmental Approval
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <FormControl fullWidth sx={{ mb: 2 }} size={isMobile ? "small" : "medium"}>
                                <InputLabel>Status</InputLabel>
                                <Select 
                                    value={departmentalForm.status} 
                                    onChange={(e) => setDepartmentalForm({ ...departmentalForm, status: e.target.value })} 
                                    label="Status"
                                >
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="approved">Approved</MenuItem>
                                    <MenuItem value="rejected">Rejected</MenuItem>
                                    <MenuItem value="not_applicable">Not Applicable</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField 
                                fullWidth 
                                label="Comments" 
                                multiline 
                                rows={3} 
                                value={departmentalForm.comments} 
                                onChange={(e) => setDepartmentalForm({ ...departmentalForm, comments: e.target.value })}
                                size={isMobile ? "small" : "medium"}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button onClick={handleCloseDepartmentalDialog} size={isMobile ? "small" : "medium"}>
                            Cancel
                        </Button>
                        <Button onClick={handleUpdateDepartmental} variant="contained" disabled={actionLoading} size={isMobile ? "small" : "medium"}>
                            {actionLoading ? <CircularProgress size={20} /> : 'Update'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Ethics Review Dialog */}
                <Dialog 
                    open={openEthicsDialog} 
                    onClose={handleCloseEthicsDialog} 
                    maxWidth="sm" 
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Ethics Committee Review
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <FormControl fullWidth sx={{ mb: 2 }} size={isMobile ? "small" : "medium"}>
                                <InputLabel>Status</InputLabel>
                                <Select 
                                    value={ethicsForm.status} 
                                    onChange={(e) => setEthicsForm({ ...ethicsForm, status: e.target.value })} 
                                    label="Status"
                                >
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="approved">Approved</MenuItem>
                                    <MenuItem value="rejected">Rejected</MenuItem>
                                    <MenuItem value="exempt">Exempt</MenuItem>
                                    <MenuItem value="not_required">Not Required</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField 
                                fullWidth 
                                label="Comments" 
                                multiline 
                                rows={3} 
                                value={ethicsForm.comments} 
                                onChange={(e) => setEthicsForm({ ...ethicsForm, comments: e.target.value })}
                                size={isMobile ? "small" : "medium"}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button onClick={handleCloseEthicsDialog} size={isMobile ? "small" : "medium"}>
                            Cancel
                        </Button>
                        <Button onClick={handleUpdateEthics} variant="contained" disabled={actionLoading} size={isMobile ? "small" : "medium"}>
                            {actionLoading ? <CircularProgress size={20} /> : 'Update'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Final Approval Dialog */}
                <Dialog 
                    open={openFinalDialog} 
                    onClose={handleCloseFinalDialog} 
                    maxWidth="sm" 
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Final Approval
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <FormControl fullWidth sx={{ mb: 2 }} size={isMobile ? "small" : "medium"}>
                                <InputLabel>Status</InputLabel>
                                <Select 
                                    value={finalForm.status} 
                                    onChange={(e) => setFinalForm({ ...finalForm, status: e.target.value })} 
                                    label="Status"
                                >
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="approved">Approved</MenuItem>
                                    <MenuItem value="rejected">Rejected</MenuItem>
                                    <MenuItem value="deferred">Deferred</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField 
                                fullWidth 
                                label="Final Comments" 
                                multiline 
                                rows={3} 
                                value={finalForm.comments} 
                                onChange={(e) => setFinalForm({ ...finalForm, comments: e.target.value })}
                                size={isMobile ? "small" : "medium"}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button onClick={handleCloseFinalDialog} size={isMobile ? "small" : "medium"}>
                            Cancel
                        </Button>
                        <Button onClick={handleUpdateFinal} variant="contained" disabled={actionLoading} size={isMobile ? "small" : "medium"}>
                            {actionLoading ? <CircularProgress size={20} /> : 'Update'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* View Details Dialog */}
                <Dialog 
                    open={openViewDialog} 
                    onClose={handleCloseViewDialog} 
                    maxWidth="md" 
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Topic Details
                    </DialogTitle>
                    <DialogContent>
                        {selectedTopic && (
                            <Box sx={{ pt: 2 }}>
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center', 
                                    mb: 2,
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    gap: { xs: 1, sm: 0 }
                                }}>
                                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {selectedTopic.topic_title}
                                    </Typography>
                                    <Chip 
                                        icon={selectedTopic.oversight_status_icon} 
                                        label={selectedTopic.formatted_oversight_status} 
                                        color={selectedTopic.oversight_status_color}
                                    />
                                </Box>
                                
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                            <strong>Student:</strong> {selectedTopic.student_name}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                            <strong>Supervisor:</strong> {selectedTopic.supervisor_name}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                            <strong>Department:</strong> {selectedTopic.department || 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                            <strong>Submitted:</strong> {formatCreatedAtDate(selectedTopic.submission_date)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                            <strong>Similarity Score:</strong> {selectedTopic.similarity_score || 0}%
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                            <strong>Priority:</strong> {selectedTopic.formatted_priority_level}
                                        </Typography>
                                    </Grid>
                                </Grid>
                                
                                <Divider sx={{ my: 2 }} />
                                
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                    Approval Statuses
                                </Typography>
                                <Grid container spacing={1} sx={{ mb: 3 }}>
                                    <Grid item xs={12} sm={4}>
                                        <Chip 
                                            label={`Department: ${selectedTopic.formatted_departmental_approval}`} 
                                            color={selectedTopic.departmental_approval_color} 
                                            size="small"
                                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Chip 
                                            label={`Ethics: ${selectedTopic.formatted_ethics_status}`} 
                                            color={selectedTopic.ethics_status_color} 
                                            size="small"
                                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Chip 
                                            label={`Final: ${selectedTopic.formatted_final_approval}`} 
                                            color={selectedTopic.final_approval_color} 
                                            size="small"
                                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                        />
                                    </Grid>
                                </Grid>
                                
                                {selectedTopic.department_comments && (
                                    <>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                            Department Comments
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                            {selectedTopic.department_comments}
                                        </Typography>
                                    </>
                                )}
                                
                                {selectedTopic.ethics_comments && (
                                    <>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                            Ethics Comments
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                            {selectedTopic.ethics_comments}
                                        </Typography>
                                    </>
                                )}
                                
                                {selectedTopic.final_comments && (
                                    <>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                            Final Comments
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                            {selectedTopic.final_comments}
                                        </Typography>
                                    </>
                                )}
                                
                                {selectedTopic.oversight_notes && (
                                    <>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                            Oversight Notes
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                            {selectedTopic.oversight_notes}
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button onClick={handleCloseViewDialog} variant="contained" size={isMobile ? "small" : "medium"}>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </FullWidthContainer>
        </>
    );
};

export default TopicOversight;